import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Users,
  UserPlus,
  Crown,
  Ban,
  CheckCircle2,
  KeyRound,
  Loader2,
  MoreVertical,
  RefreshCw,
  ShieldCheck,
  Mail,
  TrendingUp,
  TrendingDown,
  Clock,
  LogOut,
  Trash2,
  AlertTriangle,
  SlidersHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ThemeToggle } from '../components/ThemeToggle';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'dd/MM/yyyy', { locale: fr });
  } catch (_e) {
    return '—';
  }
};

const FEATURE_LIST = [
  { id: 'calendar', label: 'Calendrier' },
  { id: 'reports', label: 'Compte rendu' },
  { id: 'charges', label: 'Charge' },
  { id: 'profit', label: 'Bénéfice' },
  { id: 'invoicing', label: 'Devis & Factures' },
  { id: 'calculator', label: 'Calculatrice' },
  { id: 'forecast', label: 'Prévisionnel' },
  { id: 'impots', label: 'Impôts' },
  { id: 'archive', label: 'Archive' },
  { id: 'business', label: 'Mon entreprise' },
];

const StatusBadge = ({ user }) => {
  if (user.is_admin) {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" data-testid="badge-admin">Admin</span>;
  }
  if (user.status === 'PENDING_APPROVAL') {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800" data-testid="badge-pending">En attente</span>;
  }
  if (user.status === 'SUSPENDED') {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-testid="badge-suspended">Suspendu</span>;
  }
  if (user.subscription_status === 'UNLIMITED') {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800" data-testid="badge-unlimited">Illimité</span>;
  }
  if (user.subscription_status === 'ACTIVE') {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800" data-testid="badge-active">Abonné</span>;
  }
  if (user.subscription_status === 'TRIAL') {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-800" data-testid="badge-trial">Essai</span>;
  }
  return <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700" data-testid="badge-expired">Expiré</span>;
};

export const AdminPage = () => {
  const { api, user: currentUser, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [featuresDraft, setFeaturesDraft] = useState([]);
  const [actionUser, setActionUser] = useState(null);
  const [createForm, setCreateForm] = useState({ email: '', password: '', grant_unlimited: false });
  const [resetForm, setResetForm] = useState({ new_password: '' });
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/stats'),
      ]);
      setUsers(u.data);
      setStats(s.data);
    } catch (e) {
      toast.error('Erreur chargement utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!createForm.email || !createForm.password) {
      toast.error('Email et mot de passe requis');
      return;
    }
    if (createForm.password.length < 6) {
      toast.error('Mot de passe trop court (min 6 caractères)');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/admin/users', createForm);
      toast.success('Utilisateur créé');
      setCreateOpen(false);
      setCreateForm({ email: '', password: '', grant_unlimited: false });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}/approve`);
      toast.success(`${u.email} validé`);
      load();
    } catch (e) {
      toast.error('Erreur validation');
    }
  };

  const handleSuspend = async (u) => {
    if (!window.confirm(`Suspendre ${u.email} ? Ses données sont conservées.`)) return;
    try {
      await api.put(`/admin/users/${u.id}/suspend`);
      toast.success('Compte suspendu');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleReactivate = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}/reactivate`);
      toast.success('Compte réactivé');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleGrant = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}/grant-unlimited`);
      toast.success('Accès illimité accordé');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleRevoke = async (u) => {
    if (!window.confirm(`Retirer l'accès illimité de ${u.email} ?`)) return;
    try {
      await api.put(`/admin/users/${u.id}/revoke-unlimited`);
      toast.success('Accès illimité retiré');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleResetPassword = async () => {
    if (!resetForm.new_password || resetForm.new_password.length < 6) {
      toast.error('Mot de passe trop court (min 6)');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/users/${actionUser.id}/reset-password`, resetForm);
      toast.success('Mot de passe réinitialisé');
      setResetOpen(false);
      setResetForm({ new_password: '' });
    } catch (e) { toast.error('Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== actionUser?.email) {
      toast.error('Veuillez retaper exactement l\'email pour confirmer');
      return;
    }
    setSubmitting(true);
    try {
      await api.delete(`/admin/users/${actionUser.id}`);
      toast.success(`Compte ${actionUser.email} supprimé`);
      setDeleteOpen(false);
      setDeleteConfirm('');
      setActionUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setSubmitting(false);
    }
  };

  const openFeatures = (u) => {
    setActionUser(u);
    setFeaturesDraft(u.disabled_features || []);
    setFeaturesOpen(true);
  };

  const toggleFeature = (fid) => {
    const set = new Set(featuresDraft);
    if (set.has(fid)) set.delete(fid); else set.add(fid);
    setFeaturesDraft(Array.from(set));
  };

  const toggleAllFeatures = () => {
    if (featuresDraft.length === FEATURE_LIST.length) {
      setFeaturesDraft([]);
    } else {
      setFeaturesDraft(FEATURE_LIST.map((f) => f.id));
    }
  };

  const handleSaveFeatures = async () => {
    setSubmitting(true);
    try {
      await api.put(`/admin/users/${actionUser.id}/features`, { disabled_features: featuresDraft });
      toast.success('Restrictions mises à jour');
      setFeaturesOpen(false);
      setActionUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-page">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-amber-600" />
          <div>
            <h1 className="font-outfit text-xl font-bold text-slate-900">Tableau Admin Ledgerly</h1>
            <p className="text-xs text-slate-500">{currentUser?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button variant="outline" onClick={load} data-testid="admin-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
          <Button variant="outline" onClick={logout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card data-testid="stat-total-users">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Utilisateurs</p>
                  <p className="text-2xl font-outfit font-bold text-slate-900 mt-1">{stats?.total_users ?? '—'}</p>
                </div>
                <Users className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-pending-users">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium">En attente</p>
                  <p className="text-2xl font-outfit font-bold text-orange-600 mt-1">{stats?.pending_users ?? '—'}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-300" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-paying-users">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Abonnés payants</p>
                  <p className="text-2xl font-outfit font-bold text-emerald-700 mt-1">{stats?.paying_users ?? '—'}</p>
                </div>
                <Crown className="w-8 h-8 text-emerald-300" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-revenue">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Revenus</p>
                  <p className="text-2xl font-outfit font-bold text-[#065F46] mt-1 font-mono">{(stats?.total_revenue_eur ?? 0).toFixed(2)} €</p>
                </div>
                <TrendingUp className="w-8 h-8 text-emerald-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-outfit text-lg">Utilisateurs ({users.length})</CardTitle>
            <Button onClick={() => setCreateOpen(true)} className="bg-[#065F46] hover:bg-[#047857]" data-testid="admin-create-user-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Créer un utilisateur
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
            ) : users.length === 0 ? (
              <p className="text-center py-8 text-slate-500">Aucun utilisateur.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase">
                      <th className="text-left py-3 px-2 font-medium">Email</th>
                      <th className="text-left py-3 px-2 font-medium">Statut</th>
                      <th className="text-left py-3 px-2 font-medium">Plan</th>
                      <th className="text-left py-3 px-2 font-medium">Fin abonnement</th>
                      <th className="text-right py-3 px-2 font-medium">CA évolution</th>
                      <th className="text-left py-3 px-2 font-medium">Inscrit le</th>
                      <th className="text-right py-3 px-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                        <td className="py-3 px-2">
                          <div className="font-medium text-slate-900">{u.email}</div>
                        </td>
                        <td className="py-3 px-2"><StatusBadge user={u} /></td>
                        <td className="py-3 px-2 text-slate-600">{u.subscription_plan || '—'}</td>
                        <td className="py-3 px-2 text-slate-600 font-mono text-xs">
                          {u.subscription_status === 'TRIAL' ? `Essai → ${formatDate(u.trial_ends_at)}` : formatDate(u.subscription_ends_at)}
                        </td>
                        <td className="py-3 px-2 text-right" data-testid={`user-ca-delta-${u.id}`}>
                          {u.ca_delta_pct === null || u.ca_delta_pct === undefined ? (
                            <span className="text-slate-400 text-xs">—</span>
                          ) : u.ca_delta_pct >= 0 ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-mono font-semibold">
                              <TrendingUp className="w-3.5 h-3.5" /> +{u.ca_delta_pct}%
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600 font-mono font-semibold">
                              <TrendingDown className="w-3.5 h-3.5" /> {u.ca_delta_pct}%
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-slate-500 text-xs">{formatDate(u.created_at)}</td>
                        <td className="py-3 px-2 text-right">
                          {u.is_admin ? (
                            <span className="text-xs text-slate-400 italic">— Admin —</span>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`user-actions-${u.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {u.status === 'PENDING_APPROVAL' && (
                                  <DropdownMenuItem onClick={() => handleApprove(u)} data-testid={`approve-${u.id}`}>
                                    <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" /> Valider l'accès
                                  </DropdownMenuItem>
                                )}
                                {u.status === 'ACTIVE' && u.subscription_status !== 'UNLIMITED' && (
                                  <DropdownMenuItem onClick={() => handleGrant(u)} data-testid={`grant-${u.id}`}>
                                    <Crown className="w-4 h-4 mr-2 text-amber-600" /> Accès illimité gratuit
                                  </DropdownMenuItem>
                                )}
                                {u.subscription_status === 'UNLIMITED' && (
                                  <DropdownMenuItem onClick={() => handleRevoke(u)} data-testid={`revoke-${u.id}`}>
                                    <Crown className="w-4 h-4 mr-2 text-slate-400" /> Retirer accès illimité
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => { setActionUser(u); setResetForm({ new_password: '' }); setResetOpen(true); }} data-testid={`reset-${u.id}`}>
                                  <KeyRound className="w-4 h-4 mr-2" /> Réinitialiser mot de passe
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openFeatures(u)} data-testid={`features-${u.id}`}>
                                  <SlidersHorizontal className="w-4 h-4 mr-2 text-slate-600" /> Restreindre l'accès
                                </DropdownMenuItem>
                                {u.status !== 'SUSPENDED' ? (
                                  <DropdownMenuItem onClick={() => handleSuspend(u)} className="text-red-600" data-testid={`suspend-${u.id}`}>
                                    <Ban className="w-4 h-4 mr-2" /> Suspendre l'accès
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => handleReactivate(u)} data-testid={`reactivate-${u.id}`}>
                                    <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" /> Réactiver
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => { setActionUser(u); setDeleteConfirm(''); setDeleteOpen(true); }} className="text-red-700" data-testid={`delete-${u.id}`}>
                                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer le compte
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create user dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Créer un utilisateur</DialogTitle>
            <DialogDescription>
              L'utilisateur recevra un email avec ses identifiants.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="adm-email">Email</Label>
              <Input
                id="adm-email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                data-testid="create-user-email"
              />
            </div>
            <div>
              <Label htmlFor="adm-pwd">Mot de passe temporaire</Label>
              <Input
                id="adm-pwd"
                type="text"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                data-testid="create-user-password"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="create-user-grant-unlimited-label">
              <input
                type="checkbox"
                checked={createForm.grant_unlimited}
                onChange={(e) => setCreateForm({ ...createForm, grant_unlimited: e.target.checked })}
                data-testid="create-user-grant-unlimited"
              />
              <Crown className="w-4 h-4 text-amber-600" /> Accorder un accès <b>illimité gratuit</b> (sans Stripe)
            </label>
            {!createForm.grant_unlimited && (
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Mail className="w-3 h-3" /> Sinon : 30 jours d'essai gratuit puis abonnement requis.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={submitting} className="bg-[#065F46] hover:bg-[#047857]" data-testid="confirm-create-user">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
            <DialogDescription>
              {actionUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="new-pwd">Nouveau mot de passe</Label>
            <Input
              id="new-pwd"
              type="text"
              value={resetForm.new_password}
              onChange={(e) => setResetForm({ new_password: e.target.value })}
              data-testid="reset-password-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Annuler</Button>
            <Button onClick={handleResetPassword} disabled={submitting} data-testid="confirm-reset-password">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Réinitialiser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Features restriction dialog */}
      <Dialog open={featuresOpen} onOpenChange={setFeaturesOpen}>
        <DialogContent data-testid="features-dialog">
          <DialogHeader>
            <DialogTitle>Restreindre l'accès aux fonctionnalités</DialogTitle>
            <DialogDescription>
              {actionUser?.email} — cochez les fonctionnalités à <b>désactiver</b> pour cet utilisateur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <button
              type="button"
              onClick={toggleAllFeatures}
              className="text-xs font-medium text-[#065F46] hover:underline"
              data-testid="features-toggle-all"
            >
              {featuresDraft.length === FEATURE_LIST.length ? 'Tout réactiver' : 'Tout désactiver'}
            </button>
            <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
              {FEATURE_LIST.map((f) => {
                const disabled = featuresDraft.indexOf(f.id) !== -1;
                return (
                  <label
                    key={f.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer text-sm ${disabled ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`feature-toggle-${f.id}-label`}
                  >
                    <input
                      type="checkbox"
                      checked={disabled}
                      onChange={() => toggleFeature(f.id)}
                      data-testid={`feature-toggle-${f.id}`}
                    />
                    <span className={disabled ? 'line-through text-red-900' : 'text-slate-700'}>{f.label}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-slate-500">
              {featuresDraft.length === 0 ? 'Aucune restriction.' : `${featuresDraft.length} fonctionnalité(s) désactivée(s).`}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeaturesOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveFeatures} disabled={submitting} className="bg-[#065F46] hover:bg-[#047857] text-white" data-testid="save-features-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user dialog */}
      <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) setDeleteConfirm(''); }}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Supprimer le compte
            </DialogTitle>
            <DialogDescription>
              Cette action est <b>irréversible</b>. Le compte sera définitivement supprimé.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-900">
              Vous êtes sur le point de supprimer : <b>{actionUser?.email}</b>
            </div>
            <div>
              <Label htmlFor="delete-confirm" className="text-slate-700">
                Pour confirmer, retapez exactement l'email du compte
              </Label>
              <Input
                id="delete-confirm"
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={actionUser?.email}
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Annuler</Button>
            <Button
              onClick={handleDelete}
              disabled={submitting || deleteConfirm !== actionUser?.email}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-user"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Trash2 className="w-4 h-4 mr-2" /> Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
